// @ts-nocheck

import debounce from 'lodash/debounce';
import { useCallback, useEffect, useRef, useState } from 'react';

interface DebounceOptions {
  delay?: number;
  leading?: boolean;
  trailing?: boolean;
  maxWait?: number;
}

export function useDebounce<T extends (...args: any[]) => any>(callback: T, options?: DebounceOptions): T {
  const callbackRef = useRef(callback);
  const debouncedRef = useRef<ReturnType<typeof debounce> | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const { delay = 300, ...restOptions } = options || {};

    debouncedRef.current = debounce(
      (...args: Parameters<T>) => {
        return callbackRef.current(...args);
      },
      delay,
      restOptions
    );

    return () => {
      debouncedRef.current?.cancel();
    };
  }, [options]);

  return (debouncedRef.current || callback) as unknown as T;
}

export function useDebouncedValue<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 300,
  options?: {
    leading?: boolean;
    trailing?: boolean;
    maxWait?: number;
    onImmediate?: (...args: Parameters<T>) => void;
  }
): {
  debouncedCallback: T;
  cancel: () => void;
  flush: () => void;
} {
  const { onImmediate, ...debounceOptions } = options || {};
  const callbackRef = useRef(callback);
  const onImmediateRef = useRef(onImmediate);

  useEffect(() => {
    callbackRef.current = callback;
    onImmediateRef.current = onImmediate;
  }, [callback, onImmediate]);

  const debouncedCallback = useCallback(
    debounce(
      (...args: Parameters<T>) => {
        return callbackRef.current(...args);
      },
      delay,
      debounceOptions
    ),
    [delay, debounceOptions]
  );

  const wrappedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (onImmediateRef.current) {
        onImmediateRef.current(...args);
      }
      return debouncedCallback(...args);
    },
    [debouncedCallback]
  ) as T;

  return {
    debouncedCallback: wrappedCallback,
    cancel: debouncedCallback.cancel,
    flush: debouncedCallback.flush,
  };
}
